import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getPageBlocks, type Block } from "../../sim/cms";

/**
 * THE PROMO BANNER — the home page's rotating banner for update news and seasonal events.
 *
 * ITS SLIDES ARE ADMIN CONTENT: every active IMAGE section on the CMS page `landing-banner` (Admin → Page
 * Builder → "Landing banner") is one slide — its image, optional headline / line / button, click-through link,
 * and an optional SHOW-FROM / SHOW-UNTIL window so a seasonal event appears and retires on its own.
 *
 * NO SLIDES, NO BANNER. With nothing configured (or everything out of its window) this renders nothing at all,
 * and the page around it is designed to be complete without it — the banner is an extra, never a hole.
 *
 * Behaviour: slides flip on their own every ROTATE_MS, pausing while hovered or focused, while the tab is hidden,
 * and entirely under reduced motion; arrows, dots, the keyboard (← →) and a swipe all move it by hand.
 */

const ROTATE_MS = 6500;

interface Slide { id: number; image: string; alt: string; heading?: string; body?: string; button?: string; link?: string }

/** Is `b` showing today? Its optional window is inclusive, in the viewer's local time. */
function inWindow(b: Block, now = Date.now()): boolean {
  const from = b.config.startsAt ? Date.parse(b.config.startsAt) : NaN;
  const until = b.config.endsAt ? Date.parse(b.config.endsAt) : NaN;
  if (Number.isFinite(from) && now < from) return false;
  if (Number.isFinite(until) && now > until + 86_399_999) return false;   // "until" includes the whole day
  return true;
}

export function PromoCarousel() {
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  useEffect(() => {
    let alive = true;
    getPageBlocks("landing-banner").then((b) => { if (alive) setBlocks(b); });
    return () => { alive = false; };
  }, []);
  const slides = useMemo<Slide[]>(() => (blocks ?? [])
    .filter((b) => b.type === "banner" && b.config.imageUrl && inWindow(b))
    .map((b) => ({
      id: b.id, image: b.config.imageUrl!, alt: b.config.alt || b.config.heading || "Shelf Garden news",
      heading: b.config.heading || undefined, body: b.config.body || undefined,
      button: b.config.buttonText || undefined, link: b.config.link || undefined,
    })), [blocks]);

  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = slides.length;
  const go = useCallback((d: number) => setI((v) => (n ? (v + d + n) % n : 0)), [n]);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  // auto-advance (not under reduced motion, not while paused, not in a background tab)
  useEffect(() => {
    if (n < 2 || paused || reduced) return;
    const t = window.setInterval(() => { if (!document.hidden) go(1); }, ROTATE_MS);
    return () => window.clearInterval(t);
  }, [n, paused, reduced, go]);
  useEffect(() => { if (i >= n) setI(0); }, [i, n]);

  // swipe
  const startX = useRef<number | null>(null);
  if (n === 0) return null;

  return (
    <section
      className="lp-promo" aria-roledescription="carousel" aria-label="News and events"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}
      onKeyDown={(e) => { if (e.key === "ArrowLeft") go(-1); if (e.key === "ArrowRight") go(1); }}
      onPointerDown={(e) => { startX.current = e.clientX; }}
      onPointerUp={(e) => {
        if (startX.current == null) return;
        const dx = e.clientX - startX.current; startX.current = null;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      }}
    >
      <div className="lp-promo-track" style={{ transform: `translateX(${-i * 100}%)` }}>
        {slides.map((s, k) => {
          const inner = (
            <>
              <img src={s.image} alt={s.alt} loading={k === 0 ? "eager" : "lazy"} draggable={false} />
              {(s.heading || s.body || s.button) && (
                <div className="lp-promo-copy">
                  {s.heading && <h2>{s.heading}</h2>}
                  {s.body && <p>{s.body}</p>}
                  {s.button && <span className="lp-promo-btn">{s.button} →</span>}
                </div>
              )}
            </>
          );
          const external = s.link && /^https?:\/\//.test(s.link);
          return (
            <div key={s.id} className="lp-promo-slide" role="group" aria-roledescription="slide" aria-label={`${k + 1} of ${n}`} aria-hidden={k !== i}>
              {s.link
                ? (external
                  ? <a href={s.link} tabIndex={k === i ? 0 : -1} target="_blank" rel="noreferrer">{inner}</a>
                  : <Link to={s.link} tabIndex={k === i ? 0 : -1}>{inner}</Link>)
                : inner}
            </div>
          );
        })}
      </div>
      {n > 1 && (
        <>
          <button className="lp-promo-arrow prev" onClick={() => go(-1)} aria-label="Previous slide">‹</button>
          <button className="lp-promo-arrow next" onClick={() => go(1)} aria-label="Next slide">›</button>
          <div className="lp-promo-dots" role="tablist" aria-label="Choose a slide">
            {slides.map((s, k) => (
              <button key={s.id} role="tab" aria-selected={k === i} aria-label={`Slide ${k + 1}`}
                className={k === i ? "on" : ""} onClick={() => setI(k)} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
